import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faChalkboardUser,
  faBars,
  faTimes,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { authService } from "../../api/auth.service";

function Navbar({ page }) {
  const navigate = useNavigate();
  const [isAuthenticated] = useState(authService.isUserAuthenticated());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isHome = page === "home";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogOut = async () => {
    await authService.logout();
    navigate("/login");
  };

  const navLinks = [
    { label: "Home", to: "/", key: "home" },
    { label: "Courses", to: "/courses", key: "courses" },
    { label: "About", to: "/about", key: "about" },
    { label: "Contact", to: "/contact", key: "contact" },
    ...(isAuthenticated
      ? [
          { label: "Profile", to: "/profile", key: "profile", icon: faUser },
          { label: "Learnings", to: "/learnings", key: "learnings", icon: faChalkboardUser },
        ]
      : []),
  ];

  /* transparent on home hero, white everywhere else or after scroll */
  const isTransparent = isHome && !scrolled;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isTransparent
            ? "bg-transparent border-transparent shadow-none"
            : "bg-white border-b border-gray-100 shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[80px]">

          {/* Logo */}
          <Link to="/" className="shrink-0">
            <img
              src={logo}
              alt="EduVerse"
              className={`h-12 w-auto object-contain transition-all duration-300 ${
                isTransparent ? "brightness-0 invert" : ""
              }`}
            />
          </Link>

          {/* Desktop nav links */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.to}
                className={`relative flex items-center gap-1.5 px-4 py-2 rounded-lg text-[15px] font-semibold no-underline transition-all duration-200 group ${
                  isTransparent
                    ? page === link.key
                      ? "text-warning"
                      : "text-white/90 hover:text-warning"
                    : page === link.key
                    ? "text-primary bg-primary-light"
                    : "text-gray-600 hover:text-primary hover:bg-gray-50"
                }`}
              >
                {link.icon && (
                  <FontAwesomeIcon icon={link.icon} className="text-xs opacity-75" />
                )}
                {link.label}
                {/* active indicator */}
                <span
                  className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-300 ${
                    isTransparent ? "bg-warning" : "bg-primary"
                  } ${page === link.key ? "w-4" : "w-0 group-hover:w-4"}`}
                />
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <button
                onClick={handleLogOut}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 hover:scale-105 ${
                  isTransparent
                    ? "bg-white/15 border border-white/30 text-white hover:bg-white/25"
                    : "bg-primary text-white hover:bg-primary-dark shadow-sm shadow-primary/20"
                }`}
              >
                Sign Out
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className={`px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                    isTransparent
                      ? "text-white/90 hover:text-warning"
                      : "text-gray-600 hover:text-primary"
                  }`}
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/register")}
                  className={`group flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 hover:scale-105 ${
                    isTransparent
                      ? "bg-warning text-black hover:bg-yellow-300 shadow-md shadow-warning/30"
                      : "bg-primary text-white hover:bg-primary-dark shadow-md shadow-primary/20"
                  }`}
                >
                  Get Started
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="text-xs group-hover:translate-x-0.5 transition-transform duration-200"
                  />
                </button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-xl transition-colors duration-200 ${
              isTransparent
                ? "text-white hover:bg-white/10"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            aria-label="Toggle menu"
          >
            <FontAwesomeIcon icon={mobileOpen ? faTimes : faBars} className="text-lg" />
          </button>
        </div>

        {/* Mobile dropdown — always white */}
        <div
          className={`md:hidden bg-white border-t border-gray-100 overflow-hidden transition-all duration-300 ease-in-out ${
            mobileOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-[15px] font-semibold no-underline transition-all duration-200 ${
                  page === link.key
                    ? "bg-primary-light text-primary"
                    : "text-gray-600 hover:bg-gray-50 hover:text-primary"
                }`}
              >
                {link.icon && <FontAwesomeIcon icon={link.icon} className="w-4 text-sm" />}
                {link.label}
                {page === link.key && (
                  <span className="ml-auto w-2 h-2 rounded-full bg-primary" />
                )}
              </Link>
            ))}

            <div className="pt-3 border-t border-gray-100 space-y-2">
              {isAuthenticated ? (
                <button
                  onClick={() => { handleLogOut(); setMobileOpen(false); }}
                  className="w-full py-3 rounded-xl bg-primary text-white font-bold hover:bg-primary-dark transition-colors"
                >
                  Sign Out
                </button>
              ) : (
                <>
                  <button
                    onClick={() => { navigate("/login"); setMobileOpen(false); }}
                    className="w-full py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:border-primary hover:text-primary transition-all duration-200"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => { navigate("/register"); setMobileOpen(false); }}
                    className="w-full py-3 rounded-xl bg-primary text-white font-bold hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
                  >
                    Get Started
                    <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* spacer for non-home pages so content isn't hidden under fixed bar */}
      {!isHome && <div className="h-[102px]" />}
    </>
  );
}

export default Navbar;
